import * as XLSX from 'xlsx';
import type { ConsignmentReconciliationResponse } from '../types/venue';
import {
  buildSupplierStatementData,
  SUPPLIER_STATEMENT_NOTE,
} from './consignmentSupplierStatement';
import { fromMinorUnitsOrNull } from './money';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const fmtCost = (v: number | null): string | number =>
  v != null ? Math.round(v * 100) / 100 : '';

const safeName = (s: string): string =>
  s.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 30);

// ── Excel export ──────────────────────────────────────────────────────────────

export function downloadConsignmentSupplierExcel(
  report: ConsignmentReconciliationResponse,
): void {
  const d = buildSupplierStatementData(report);

  const COLS = 7;
  const span = (text: string | number): (string | number)[] =>
    [text, ...Array(COLS - 1).fill('')];

  const rows: (string | number)[][] = [
    span('SUPPLIER CONSIGNMENT STATEMENT'),
    span(d.statementNumber),
    Array(COLS).fill(''),
    ['Consignor',  d.supplierName ?? '—', '', '', '', '', ''],
    ['Account',    d.accountName,         '', '', '', '', ''],
    ['Period',     `${fmtDate(d.periodFrom)} – ${fmtDate(d.periodTo)}`, '', '', '', '', ''],
    ['Generated',  fmtDate(d.generatedAt), '', '', '', '', ''],
    Array(COLS).fill(''),
    // Column headers
    ['Item', 'Opening Stock', 'Received', 'Closing Stock', 'Consumed', 'Unit Cost', 'Amount'],
    // Data rows
    ...d.rows.map(r => [
      r.displayName,
      r.openingQty,
      r.receivedQty,
      r.closingQty,
      r.consumedQty,
      fmtCost(fromMinorUnitsOrNull(r.unitCost)),
      fmtCost(fromMinorUnitsOrNull(r.amount)),
    ]),
    Array(COLS).fill(''),
    // Totals
    ['', '', '', '', d.totalConsumed, '', d.totalAmount != null ? d.totalAmount : ''],
    Array(COLS).fill(''),
    span(SUPPLIER_STATEMENT_NOTE),
  ];

  // Total row index (0-based): header block (8 rows) + data rows + blank
  const headerRows = 9;              // rows 0-8 (index 0 = title)
  const dataRowCount = d.rows.length;
  const totalRowIdx = headerRows + dataRowCount + 1; // +1 for blank separator

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Column widths
  ws['!cols'] = [
    { wch: 42 }, // Item
    { wch: 14 }, // Opening Stock
    { wch: 12 }, // Received
    { wch: 14 }, // Closing Stock
    { wch: 12 }, // Consumed
    { wch: 12 }, // Unit Cost
    { wch: 14 }, // Amount
  ];

  // Bold the title and totals label via cell styles (open-source XLSX supports
  // basic z property but not full styles — we use a comment cell to mark totals)
  const totalCell = XLSX.utils.encode_cell({ r: totalRowIdx, c: 4 });
  if (ws[totalCell]) {
    ws[totalCell].z = '#,##0';
  }
  const amountCell = XLSX.utils.encode_cell({ r: totalRowIdx, c: 6 });
  if (ws[amountCell]) {
    ws[amountCell].z = '#,##0.00';
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Supplier Statement');

  const datePart = d.periodTo.slice(0, 10);
  XLSX.writeFile(wb, `SupplierStatement-${safeName(d.accountName)}-${datePart}.xlsx`);
}
